import * as selectGroupTypes from "../SelectGrouped/types";
import React from "react";
import SelectGrouped from "../SelectGrouped";
import Swipable from "./Swipable";
import cardCategories, {CardCategory, Card} from "./cardCategories";
import cn from "../../lib/classnames";
import styles from "./CardPicker.module.css";
import {getCards} from "../../lib/getCards";

const strings = {
    cardImage: "Card Image",
    selectCard: "Select Card",
    chooseYourCard: "Choose your card.",
    switchTo: "Switch card category to",
};

type Props = {
    cardId: string;
    name?: string;
    onChange: (cardId: string) => void;
    validationMessage?: string[];
    cardTypes: string[];
};

function cardToCardOption(card: Card): selectGroupTypes.Option {
    return {
        id: card.value,
        label: card.name,
    };
}

function categoryToCategoryOption(category: CardCategory): selectGroupTypes.OptionGroup {
    return {
        label: category.value,
        id: category.id,
        options: category.cards.map(cardToCardOption),
    };
}

function getCardCategory(cardId: string): CardCategory | undefined {
    return cardCategories.find((category) => {
        return category.cards.findIndex((c) => c.value === cardId) !== -1;
    });
}

export default function CardPicker({cardId, name, onChange, validationMessage, cardTypes}: Props) {
    const {cards, cardCategories: categories} = getCards(cardTypes);
    const cardOptGroups = categories.map(categoryToCategoryOption);

    if (cards.length === 0) {
        return null;
    }

    let cardIndex = cards.findIndex((c) => c.value === cardId); // default to 1st
    cardIndex = cardIndex > 0 ? cardIndex : 0;

    const leftCardIndex = cardIndex > 0 ? cardIndex - 1 : cards.length - 1;
    const rightCardIndex = cardIndex < cards.length - 1 ? cardIndex + 1 : 0;
    const leftCard = cards[leftCardIndex];
    const rightCard = cards[rightCardIndex];

    const currentCardId = cards[cardIndex].value;

    const cardCategory = getCardCategory(currentCardId);
    const leftCardCategory = getCardCategory(leftCard.value);
    const rightCardCategory = getCardCategory(rightCard.value);
    if (!cardCategory || !leftCardCategory || !rightCardCategory) {
        return null;
    }

    return (
        <>
            <div className={styles.swipableContainer}>
                <button
                    className={styles.swipeLeftButton}
                    onClick={() => onChange(leftCard.value)}
                    aria-label={`${strings.switchTo} ${leftCard.name}`}
                    type="button"
                >{`<`}</button>
                <Swipable
                    onSwipeRight={() => onChange(leftCard.value)}
                    onSwipeLeft={() => onChange(rightCard.value)}
                    options={cards}
                    optionIndex={cardIndex}
                    name={name}
                    currentTextColor={cardCategory.textColor}
                    rightTextColor={rightCardCategory.textColor}
                    leftTextColor={leftCardCategory.textColor}
                />
                <button
                    className={styles.swipeRightButton}
                    onClick={() => onChange(rightCard.value)}
                    aria-label={`${strings.switchTo} ${rightCard.name}`}
                    type="button"
                >{`>`}</button>
            </div>
            <div className={styles.chooseText}>{strings.chooseYourCard}</div>
            <SelectGrouped
                aria-label={strings.selectCard}
                className={cn(styles.cardPickerSelect, styles.cardPickerSelectSpecific)}
                id="card-select"
                label=""
                optionGroups={cardOptGroups}
                selectedId={currentCardId}
                onChange={(e) => {
                    const value = e.currentTarget.value;
                    onChange(value);
                }}
                validationMessage={validationMessage}
                useLargeChevron={true}
            />
        </>
    );
}
